import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApi } from "@/shared/utils/request";
import { unitsFromSeconds } from "@/shared/utils/units";
import { visibleMatterCodes } from "@/shared/utils/firmCodes";
import type { ApiClient } from "../types";
import {
	type EntryFilterOptions,
	fetchEntryFilters,
	useEntryFiltersCache,
} from "./entryFilters";
import type { BillableCategory, BillingCategory, Matter } from "./matters";

export type EntryStatus = "tracked" | "reviewed" | "exported" | "deleted";

export type EntryMatter = {
	id: string | null;
	code: string | null;
	description: string | null;
	client: string | null;
};

export type EntryCodes = {
	task: string | null;
	activity: string | null;
	taskId?: string | null;
	activityId?: string | null;
};

/** Backend child/source entry nested under an `entry_group` row. */
export type EntryGroupChildEntryWire = {
	id: string;
	applicationId?: string | number | null;
	canonicalName?: string | null;
	canonical_name?: string | null;
	summary?: string | null;
	source?: string | null;
	startTime?: string | null;
	endTime?: string | null;
	createdAt?: string | null;
	modifiedAt?: string | null;
	durationSeconds?: number | null;
	actualDurationSeconds?: number | null;
	clientId?: string | null;
	matterId?: string | null;
};

/** Backend grouped billable resource returned by GET /v2/entry-groups. */
export type EntryGroupWire = {
	object?: "entry_group" | string;
	id: string;
	status: EntryStatus;
	type?: string | null;
	narrative?: string | null;
	predictedNarrative?: string | null;
	isNarrativeDirty?: boolean | null;
	narrativeSourceEntryIds?: string[] | null;
	narrativeSourceDirtyAt?: string | null;
	narrativeGeneratedAt?: string | null;
	narrativeEditedAt?: string | null;
	durationSeconds?: number | null;
	isDurationDirty?: boolean | null;
	matterId?: string | null;
	clientId?: string | null;
	profId?: string | null;
	taskCode?: {
		id?: string | null;
		code?: string | null;
		description?: string | null;
	} | null;
	activityCode?: {
		id?: string | null;
		code?: string | null;
		description?: string | null;
	} | null;
	lastEntryAt?: string | null;
	createdAt?: string | null;
	modifiedAt?: string | null;
	entries?: EntryGroupChildEntryWire[];
};

/** Stripe-style list envelope returned by GET /v2/entry-groups. */
export type EntryGroupListWire = {
	object: "list";
	url: string;
	hasMore: boolean;
	data: EntryGroupWire[];
};

/**
 * A single source entry shown inside an entry group. This is a UI
 * projection of a backend child `ent_*` entry; ids are backend ids, not mock
 * `mtx_*` ids.
 */
export type EntryMiniTransaction = {
	id: string;
	applicationId: string | null;
	application: string | null;
	canonicalName?: string | null;
	source?: string | null;
	clientId?: string | null;
	matterId?: string | null;
	date: string; // YYYY-MM-DD
	time: string | null; // HH:mm wall-clock
	summary: string;
	units: number;
	unitsSeconds?: number | null;
	/** Raw measured seconds for this source entry (pre billing-grid rounding). */
	actualDurationSeconds: number;
	modifiedAt?: string | null;
	isNewSinceNarrativeGeneration?: boolean;
};

export type EntryGroupEntry = {
	object: "entry_group" | "entry";
	id: string;
	status: EntryStatus;
	date: string;
	/** ISO timestamp of the last non-deleted child entry's start time. */
	lastEntryAt?: string | null;
	startTime: string | null;
	endTime: string | null;
	units: number;
	unitsSeconds: number | null;
	isDurationDirty?: boolean;
	description: string;
	predictedNarrative?: string | null;
	isNarrativeDirty?: boolean;
	narrativeSourceEntryIds?: string[];
	narrativeSourceDirtyAt?: string | null;
	narrativeGeneratedAt?: string | null;
	narrativeEditedAt?: string | null;
	application: string | null;
	documentName: string | null;
	/** Timekeeper professional id for manager/timekeeper entry views. */
	timekeeperId?: string | null;
	billable: boolean | null;
	billingCategory: BillingCategory;
	billableCategory: BillableCategory;
	matter: EntryMatter | null;
	codes: EntryCodes;
	groupType?: string | null;
	miniTransactions?: EntryMiniTransaction[];
};

/**
 * Backwards-compatible name for the existing entry-group table. The row is an
 * entry group (`eg_*`) with nested child/source entries (`ent_*`), not a flat
 * `/v2/entries` list item.
 */
export type AutoCombineEntryV2 = EntryGroupEntry & {
	object: "entry_group";
	predictedNarrative: string | null;
	isNarrativeDirty: boolean;
	isDurationDirty: boolean;
	narrativeSourceEntryIds: string[];
	narrativeSourceDirtyAt: string | null;
	narrativeGeneratedAt: string | null;
	narrativeEditedAt: string | null;
	miniTransactions: EntryMiniTransaction[];
};

const PAGE_LIMIT = 1000;
const MAX_PAGES = 200;
const ENTRY_GROUPS_STALE_MS = 60 * 1000;
const EMPTY_ENTRY_GROUP_ENTRIES: AutoCombineEntryV2[] = [];

const entryGroupsRequestCache = new Map<
	string,
	Promise<AutoCombineEntryV2[]>
>();

type EntryGroupsCacheOptions = {
	filters?: EntryFilterOptions;
	timekeeperId?: string | null;
};

function entryGroupsRequestCacheKey(timekeeperId: string | null | undefined) {
	return timekeeperId ? `timekeeper:${timekeeperId}` : "default";
}

export function hasStaleNarrative(entry: EntryGroupEntry): boolean {
	return Boolean(entry.narrativeSourceDirtyAt);
}

export function hasManualNarrative(entry: EntryGroupEntry): boolean {
	return entry.isNarrativeDirty === true;
}

export function canAutoRegenerateNarrative(entry: EntryGroupEntry): boolean {
	return (
		hasStaleNarrative(entry) &&
		!hasManualNarrative(entry) &&
		(entry.status === "tracked" || entry.status === "reviewed")
	);
}

function datePart(iso: string | null | undefined): string {
	return iso?.slice(0, 10) || "";
}

function timePart(iso: string | null | undefined): string | null {
	if (!iso) return null;
	const match = iso.match(/T(\d{2}:\d{2})/) ?? iso.match(/^(\d{2}:\d{2})/);
	return match?.[1] ?? null;
}

function compareMiniTransactions(
	a: EntryMiniTransaction,
	b: EntryMiniTransaction,
): number {
	const aKey = `${a.date || "9999-99-99"}T${a.time ?? "99:99"}`;
	const bKey = `${b.date || "9999-99-99"}T${b.time ?? "99:99"}`;
	const timeOrder = aKey.localeCompare(bKey);
	return timeOrder || a.id.localeCompare(b.id);
}

function parseTimestampMillis(value: string | null | undefined): number | null {
	if (!value) return null;
	const timestamp = Date.parse(value);
	return Number.isNaN(timestamp) ? null : timestamp;
}

function isChildEntryNewSinceNarrativeGeneration(
	group: EntryGroupWire,
	child: EntryGroupChildEntryWire,
): boolean {
	// We only surface this marker when the group is currently known to be stale;
	// otherwise a manual narrative edit that accepted the current source could make
	// old child-entry modifications look "new" relative to the last AI generation.
	if (!group.narrativeSourceDirtyAt) return false;
	const generatedAt = parseTimestampMillis(group.narrativeGeneratedAt);
	if (generatedAt === null) return false;

	const sourceEntryIds = new Set(group.narrativeSourceEntryIds ?? []);
	const wasAddedAfterGeneration =
		sourceEntryIds.size > 0 && !sourceEntryIds.has(child.id);
	const modifiedAt = parseTimestampMillis(child.modifiedAt ?? child.createdAt);
	const wasUpdatedAfterGeneration =
		modifiedAt !== null && modifiedAt > generatedAt;

	return wasAddedAfterGeneration || wasUpdatedAfterGeneration;
}

function buildMatterMap(matters: Matter[]): Map<string, Matter> {
	return new Map(matters.map((m) => [m.matter_id, m]));
}

function matterForId(
	matterId: string | null | undefined,
	clientId: string | null | undefined,
	matters: Map<string, Matter>,
): EntryMatter | null {
	if (!matterId) return null;
	const matched = matters.get(matterId);
	if (!matched) {
		return {
			id: matterId,
			code: matterId,
			description: null,
			client: clientId ?? null,
		};
	}
	return {
		id: matched.matter_id,
		code: matched.matter,
		description: matched.description,
		client: matched.client,
	};
}

function matterFor(
	group: EntryGroupWire,
	matters: Map<string, Matter>,
): EntryMatter | null {
	return matterForId(group.matterId, group.clientId, matters);
}

function billingForId(
	matterId: string | null | undefined,
	matters: Map<string, Matter>,
): {
	billable: boolean | null;
	billingCategory: BillingCategory;
	billableCategory: BillableCategory;
} {
	const matched = matterId ? matters.get(matterId) : undefined;
	const billableCategory = matched?.billable_category ?? "unknown";
	return {
		billable:
			billableCategory === "unknown"
				? null
				: billableCategory !== "non_billable",
		billingCategory: matched?.billing_category ?? "unknown",
		billableCategory,
	};
}

function billingFor(
	group: EntryGroupWire,
	matters: Map<string, Matter>,
): {
	billable: boolean | null;
	billingCategory: BillingCategory;
	billableCategory: BillableCategory;
} {
	return billingForId(group.matterId, matters);
}

function sameMatter(a: EntryMatter | null, b: EntryMatter | null): boolean {
	return (
		a?.id === b?.id &&
		a?.code === b?.code &&
		a?.description === b?.description &&
		a?.client === b?.client
	);
}

function remapEntryMatter(
	entry: AutoCombineEntryV2,
	matters: Map<string, Matter>,
): AutoCombineEntryV2 {
	const matterId = entry.matter?.id;
	if (!matterId) return entry;
	const matter = matterForId(matterId, entry.matter?.client, matters);
	const billing = billingForId(matterId, matters);
	const codes = visibleMatterCodes(entry.codes, matters.get(matterId));
	if (
		codes === entry.codes &&
		sameMatter(entry.matter, matter) &&
		entry.billable === billing.billable &&
		entry.billingCategory === billing.billingCategory &&
		entry.billableCategory === billing.billableCategory
	) {
		return entry;
	}
	return { ...entry, matter, codes, ...billing };
}

function remapEntryApplications(
	entry: AutoCombineEntryV2,
	applications: Map<string, string>,
): AutoCombineEntryV2 {
	if (applications.size === 0) return entry;
	let changed = false;
	const miniTransactions = entry.miniTransactions.map((mini) => {
		if (!mini.applicationId) return mini;
		const application =
			applications.get(mini.applicationId) ?? mini.applicationId;
		if (application === mini.application) return mini;
		changed = true;
		return { ...mini, application };
	});
	return changed ? { ...entry, miniTransactions } : entry;
}

function remapEntries(
	entries: AutoCombineEntryV2[],
	matters: Matter[],
	applications: Map<string, string>,
): AutoCombineEntryV2[] {
	if (entries.length === 0) return entries;
	const matterMap = matters.length > 0 ? buildMatterMap(matters) : null;
	return entries.map((entry) => {
		const withMatters = matterMap ? remapEntryMatter(entry, matterMap) : entry;
		return remapEntryApplications(withMatters, applications);
	});
}

function applicationLabelFor(
	applicationId: string | null,
	applications: Map<string, string>,
): string | null {
	if (applicationId == null) return null;
	return applications.get(applicationId) ?? applicationId;
}

export function entryGroupToEntryGroupEntry(
	group: EntryGroupWire,
	opts: { matters: Map<string, Matter>; applications: Map<string, string> },
): AutoCombineEntryV2 {
	const miniTransactions = (group.entries ?? [])
		.map((child) => {
			const seconds = child.durationSeconds ?? 0;
			const applicationId =
				child.applicationId == null ? null : String(child.applicationId);
			return {
				id: child.id,
				applicationId,
				application: applicationLabelFor(applicationId, opts.applications),
				canonicalName: child.canonicalName ?? child.canonical_name ?? null,
				source: child.source ?? null,
				clientId: child.clientId ?? null,
				matterId: child.matterId ?? null,
				date: datePart(child.startTime) || datePart(child.endTime),
				time: timePart(child.startTime),
				summary: child.summary ?? "",
				units: unitsFromSeconds(seconds),
				unitsSeconds: seconds,
				actualDurationSeconds: child.actualDurationSeconds ?? seconds,
				modifiedAt: child.modifiedAt ?? null,
				isNewSinceNarrativeGeneration: isChildEntryNewSinceNarrativeGeneration(
					group,
					child,
				),
			};
		})
		.sort(compareMiniTransactions);
	const first = miniTransactions[0];
	const last = miniTransactions[miniTransactions.length - 1];
	// Backend durationSeconds is already aligned to the matter's billing grid.
	// Only the legacy child-sum fallback should be rounded in the browser.
	const groupDuration = group.durationSeconds;
	const unitsSeconds =
		groupDuration ??
		miniTransactions.reduce((sum, m) => sum + (m.unitsSeconds ?? 0), 0);
	const units =
		groupDuration != null
			? Number((groupDuration / 3600).toFixed(2))
			: unitsFromSeconds(unitsSeconds);
	const billing = billingFor(group, opts.matters);
	return {
		object: "entry_group",
		id: group.id,
		status: group.status,
		date: datePart(group.lastEntryAt) || last?.date || "",
		lastEntryAt: group.lastEntryAt ?? null,
		startTime: first?.time ?? null,
		endTime: last?.time ?? null,
		units,
		unitsSeconds,
		isDurationDirty: group.isDurationDirty ?? false,
		description: group.narrative ?? "",
		predictedNarrative: group.predictedNarrative ?? null,
		isNarrativeDirty: group.isNarrativeDirty ?? false,
		narrativeSourceEntryIds: group.narrativeSourceEntryIds ?? [],
		narrativeSourceDirtyAt: group.narrativeSourceDirtyAt ?? null,
		narrativeGeneratedAt: group.narrativeGeneratedAt ?? null,
		narrativeEditedAt: group.narrativeEditedAt ?? null,
		application: "Merge",
		documentName: null,
		timekeeperId: group.profId ?? null,
		matter: matterFor(group, opts.matters),
		codes: visibleMatterCodes(
			{
				task: group.taskCode?.code ?? null,
				activity: group.activityCode?.code ?? null,
				taskId: group.taskCode?.id ?? null,
				activityId: group.activityCode?.id ?? null,
			},
			group.matterId ? opts.matters?.get(group.matterId) : undefined,
		),
		groupType: group.type ?? null,
		miniTransactions,
		...billing,
	};
}

export const entryGroupToAutoCombineEntry = entryGroupToEntryGroupEntry;

/**
 * A live placeholder row for a running smart timer whose group has no captured
 * entries yet (or hasn't returned from the entry-groups refetch). Built through
 * the real mapper so billing/matter defaults match a normal empty group; the
 * row's duration ticks client-side from the timer span, not from this value.
 */
export function buildRunningTimerRow(
	entryGroupId: string,
	date: string,
): AutoCombineEntryV2 {
	const base = entryGroupToEntryGroupEntry(
		{ id: entryGroupId, status: "tracked", type: "group", entries: [] },
		{ matters: new Map(), applications: new Map() },
	);
	return {
		...base,
		date,
		// Pre-populate as a .1 timer entry (the billing minimum) so the group reads
		// as a real entry immediately; the Units cell then ticks up live from here.
		units: 0.1,
		unitsSeconds: 360,
		description: "Timer running…",
		application: "Timer",
	};
}

function applicationMapFromFilters(
	filters: Awaited<ReturnType<typeof fetchEntryFilters>>,
): Map<string, string> {
	return new Map(
		(filters.applicationOptions ?? []).map((o) => [o.value, o.label]),
	);
}

export async function fetchAllEntryGroups(
	api: ApiClient,
	matters: Matter[] = [],
	options: EntryGroupsCacheOptions = {},
): Promise<AutoCombineEntryV2[]> {
	const cacheKey = entryGroupsRequestCacheKey(options.timekeeperId);
	const cached = entryGroupsRequestCache.get(cacheKey);
	if (cached) return cached;

	const promise = fetchAllEntryGroupsUncached(api, matters, options).finally(
		() => {
			if (entryGroupsRequestCache.get(cacheKey) === promise)
				entryGroupsRequestCache.delete(cacheKey);
		},
	);
	entryGroupsRequestCache.set(cacheKey, promise);
	return promise;
}

async function fetchAllEntryGroupsUncached(
	api: ApiClient,
	matters: Matter[] = [],
	options: EntryGroupsCacheOptions = {},
): Promise<AutoCombineEntryV2[]> {
	const filters = options.filters ?? (await fetchEntryFilters(api));
	const out: EntryGroupWire[] = [];
	let startingAfter: string | null = null;
	for (let page = 0; page < MAX_PAGES; page++) {
		const queryParams: Record<string, string | number> = { limit: PAGE_LIMIT };
		if (startingAfter) queryParams.startingAfter = startingAfter;
		if (options.timekeeperId) queryParams.timekeeperIds = options.timekeeperId;
		const res = await api.makeApiRequest({
			endpoint: "/v2/entry-groups",
			method: "GET",
			queryParams,
		});
		if (res.status === "ERROR")
			throw new Error(res.error || "Failed to fetch entry groups");
		const body = res.data as EntryGroupListWire | undefined;
		if (!body || !Array.isArray(body.data)) break;
		out.push(...body.data);
		if (!body.hasMore || body.data.length === 0) break;
		startingAfter = body.data[body.data.length - 1].id;
	}
	return out.map((group) =>
		entryGroupToEntryGroupEntry(group, {
			matters: buildMatterMap(matters),
			applications: applicationMapFromFilters(filters),
		}),
	);
}

export const entryGroupsQueryKey = ["entry_groups"] as const;
export const entriesAutoCombineQueryKey = entryGroupsQueryKey;

export const fetchAllAutoCombineEntries = fetchAllEntryGroups;

export function entryGroupsScopedQueryKey(timekeeperId?: string | null) {
	return [...entryGroupsQueryKey, timekeeperId ?? "default"] as const;
}

export function entryGroupsQueryOptions(
	api: ApiClient,
	matters: Matter[] = [],
	enabled = true,
	options: EntryGroupsCacheOptions = {},
) {
	return queryOptions({
		queryKey: entryGroupsScopedQueryKey(options.timekeeperId),
		queryFn: () => fetchAllEntryGroups(api, matters, options),
		enabled,
		staleTime: ENTRY_GROUPS_STALE_MS,
	});
}

export const entriesAutoCombineQueryOptions = entryGroupsQueryOptions;

export function useEntryGroupsCache(
	matters: Matter[] = [],
	enabled = true,
	options: Omit<EntryGroupsCacheOptions, "filters"> = {},
) {
	const api = useApi();
	const { entryFilters } = useEntryFiltersCache();
	const query = useQuery({
		...entryGroupsQueryOptions(api, matters, enabled, {
			timekeeperId: options.timekeeperId,
		}),
		queryFn: () =>
			fetchAllEntryGroups(api, matters, {
				filters: entryFilters,
				timekeeperId: options.timekeeperId,
			}),
	});

	const entries = useMemo(
		() =>
			remapEntries(
				query.data ?? EMPTY_ENTRY_GROUP_ENTRIES,
				matters,
				applicationMapFromFilters(entryFilters),
			),
		[query.data, matters, entryFilters],
	);
	const isInitialLoading = query.isLoading && !query.dataUpdatedAt;
	const lastFetchedAt = query.dataUpdatedAt;
	return {
		entries,
		isLoading: isInitialLoading,
		lastFetchedAt,
	};
}

export const useEntriesAutoCombineCache = useEntryGroupsCache;
